import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Badge,
  Text,
  RefreshIcon,
  EditIcon,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
} from '@razorpay/blade/components';
import api from 'api';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { useQuery, useQueryClient } from 'react-query';
import { salaryComponentParams, salaryComponentsQueries } from '../../constants';
import { NonPayableBenefitsComponentType } from 'components/SalaryComponents/types';
import React, { useState } from 'react';
import { EmptyView } from '../../components/EmptyView';

export const NonPayableBenefitsDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openPredefinedEditPage, openNonPayableBenefitsEditPage } = useSalaryComponentHelpers();
  const queryClient = useQueryClient();
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const isOpen = !!salaryComponentId;

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  const {
    data: componentData,
    isLoading,
    refetch,
    isError,
  } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery],
    queryFn: () =>
      api.salaryComponents.queries.getSalaryComponent<NonPayableBenefitsComponentType>({
        id: salaryComponentId!,
        category: 'EARNINGS_CATEGORY',
      }),
    enabled: isOpen,
    select(data) {
      return data.payrollGetSalaryComponent.data.salaryComponent as NonPayableBenefitsComponentType;
    },
    cacheTime: 0,
  });

  const onDismiss = () => {
    queryClient.invalidateQueries({
      queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery, salaryComponentId],
    });
    closeModal();
    closeDetailsView();
  };

  const { behaviour } = componentData?.settings || {};
  const { displayBehaviour } = behaviour || {};

  const handleEditClick = () => {
    if (componentData?.isPredefined) {
      openPredefinedEditPage(salaryComponentId!);
    } else {
      openNonPayableBenefitsEditPage(salaryComponentId!);
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
        <DrawerHeader
          title={componentData?.name || 'Component Details'}
          subtitle={componentData?.description || ''}
          titleSuffix={
            componentData?.isActive ? (
              <Badge color="positive">Active</Badge>
            ) : (
              <Badge color="notice">Inactive</Badge>
            )
          }
        />

        <DrawerBody>
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Text>Loading...</Text>
            </Box>
          )}

          {isError && (
            <EmptyView
              description={
                <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                  Due to an unknown error, we could not load the component details page.
                </Text>
              }>
              <Box display={'flex'} justifyContent={'center'}>
                <Box width={'104px'}>
                  <Button icon={RefreshIcon} size={'small'} onClick={refetch}>
                    Retry
                  </Button>
                </Box>
              </Box>
            </EmptyView>
          )}

          {componentData && (
            <Box display="flex" flexDirection="column" gap="spacing.6" flex="1">
              {/* Details Section */}
              <Box display="flex" flexDirection="column" gap="spacing.6">
                {/* General Details Header */}
                <Box display="flex" flexDirection="row" gap="spacing.2" alignItems="center">
                  <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                    General
                  </Text>
                  <Text size="medium" weight="regular" color="surface.text.gray.muted">
                    details
                  </Text>
                </Box>

                {/* Details Content */}
                <Box display="flex" flexDirection="column" gap="spacing.3">
                  <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="left">
                    <InfoItem>
                      <InfoItemKey>Component Name</InfoItemKey>
                      <InfoItemValue>
                        <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                          {componentData.name || ''}
                        </Text>
                      </InfoItemValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemKey>Display name</InfoItemKey>
                      <InfoItemValue>
                        <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                          {displayBehaviour?.displayName || componentData.name || ''}
                        </Text>
                      </InfoItemValue>
                    </InfoItem>
                  </InfoGroup>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            padding="spacing.6"
            borderTopWidth="thin"
            borderTopColor="surface.border.gray.muted"
            marginLeft={'-20px'}
            position={'absolute'}
            width={'calc(100% + 20px)'}
            paddingRight={'spacing.9'}
            bottom={'spacing.0'}>
            <Box display="flex" flexDirection="row" gap="spacing.4" alignItems="center">
              <Button
                isFullWidth={true}
                size="large"
                iconPosition="left"
                icon={EditIcon}
                accessibilityLabel="Modify component"
                onClick={handleEditClick}>
                Modify component
              </Button>
              {!componentData?.isPredefined && (
                <Button
                  variant="secondary"
                  color='positive'
                  size="large"
                  accessibilityLabel={componentData?.isActive ? 'Disable' : 'Enable'}
                  onClick={() => openModal(componentData?.isActive ? 'disable' : 'enable')}>
                  {componentData?.isActive ? 'Disable' : 'Enable'}
                </Button>
              )}
            </Box>
          </Box>
        </DrawerBody>
      </Drawer>

      {componentData && (
        <ActionModals
          salaryComponent={componentData}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, it can be used in an employee's salary structure.{' '}
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once disabled, it cannot be used in an employee's salary structure.
                </Text>
              </Box>
            </Box>
          }
        />
      )}
    </>
  );
};

export default NonPayableBenefitsDetailView;
